import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Package,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
} from "lucide-react";
import { getCartAnalyticsThunk } from "../../store/thunks/adminCartThunks";
import LoadingSpinner from "../../components/common/Loader";

const CartDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartAnalytics, loadingAnalytics, error } = useSelector(
    (state) => state.cart
  );

  useEffect(() => {
    dispatch(getCartAnalyticsThunk());
  }, [dispatch]);

  if (loadingAnalytics) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="p-6 bg-red-50 text-red-600 rounded-lg">
        Error loading analytics: {error}
      </div>
    );
  }

  const stats = cartAnalytics?.overall || {};
  const exchangeStats = cartAnalytics?.exchangeItems || [];
  const abandonedByAge = cartAnalytics?.abandonedCartsByAge || [];

  const quickActions = [
    {
      title: "View All Carts",
      description: "Browse and manage user carts",
      icon: ShoppingCart,
      color: "bg-blue-500",
      path: "/cart/list",
    },
    {
      title: "Pending Verifications",
      description: "Exchange items awaiting verification",
      icon: Clock,
      color: "bg-yellow-500",
      path: "/cart/exchange-items",
      badge: exchangeStats.find((s) => s._id === "PENDING_VERIFICATION")?.count,
    },
    {
      title: "Abandoned Carts",
      description: "Recover abandoned carts",
      icon: AlertTriangle,
      color: "bg-orange-500",
      path: "/cart/abandoned",
      badge: stats.abandonedCarts,
    },
    {
      title: "Cart Analytics",
      description: "View detailed analytics",
      icon: TrendingUp,
      color: "bg-green-500",
      path: "/cart/analytics",
    },
  ];

  const statCards = [
    {
      label: "Total Carts",
      value: stats.totalCarts || 0,
      icon: ShoppingCart,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      label: "Active Carts",
      value: stats.activeCarts || 0,
      icon: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      label: "Conversion Rate",
      value: stats.conversionRate || "0%",
      icon: TrendingUp,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      label: "With Exchange",
      value: stats.cartsWithExchange || 0,
      icon: Package,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Cart Management</h1>
        <p className="text-gray-600 mt-2">
          Monitor and manage customer shopping carts and exchange items
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {quickActions.map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.path)}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 text-left relative"
          >
            {action.badge > 0 && (
              <span className="absolute top-4 right-4 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {action.badge}
              </span>
            )}
            <div
              className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center mb-4`}
            >
              <action.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{action.title}</h3>
            <p className="text-sm text-gray-600">{action.description}</p>
          </button>
        ))}
      </div>

      {/* Exchange Items Status */}
      {exchangeStats.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Exchange Items Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {exchangeStats.map((stat, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <span className="text-sm font-medium text-gray-600">
                  {stat._id?.replace(/_/g, " ") || "Unknown"}
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {stat.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Abandoned Cart Age Distribution */}
      {abandonedByAge.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">
            Abandoned Cart Age Distribution
          </h2>
          <div className="space-y-3">
            {abandonedByAge.map((age, index) => (
              <div key={index} className="flex items-center">
                <span className="text-sm font-medium text-gray-600 w-24">
                  {age._id === "90+" ? "90+ days" : `${age._id} days`}
                </span>
                <div className="flex-1 mx-4 bg-gray-200 rounded-full h-6 relative">
                  <div
                    className="absolute top-0 left-0 h-full bg-blue-500 rounded-full"
                    style={{
                      width: `${
                        (age.count /
                          Math.max(...abandonedByAge.map((a) => a.count))) *
                        100
                      }%`,
                    }}
                  />
                </div>
                <span className="text-sm font-bold text-gray-900 w-12 text-right">
                  {age.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CartDashboard;
